import { cn } from '@/lib/infra/utils';
import {
  resultSeverity,
  type OrderResultLine,
  type OrderResultSeverity,
} from './oscarResultPresentation';

/**
 * #474 — the order ticket's single result slot.
 *
 * Presentation only: it renders the line it is handed and classifies nothing.
 * Every Kalshi, Polymarket and Oscar outcome in the ticket comes through here,
 * so the tone scale and the announcement rules are decided in one place.
 *
 * The tones match the durable Overtake receipt below it (see the state classes
 * in OscarSequenceReceipt) — the transient line and the audit card must not
 * disagree about how bad the same sequence was.
 */

const SEVERITY_CLASS: Record<OrderResultSeverity, string> = {
  success: 'bg-positive/10 text-positive',
  warning: 'bg-amber-500/10 text-amber-500',
  critical: 'bg-negative/10 text-negative',
};

export interface OrderResultBannerProps {
  result: OrderResultLine | null;
}

export function OrderResultBanner({ result }: OrderResultBannerProps) {
  if (!result) return null;
  const severity = resultSeverity(result);
  return (
    <div
      data-testid="order-result"
      data-severity={severity}
      // Absent on a plain placement — only Oscar names a wording class.
      data-kind={result.kind}
      // A rejection or an exposed level interrupts; a success waits its turn.
      role={severity === 'success' ? 'status' : 'alert'}
      {...(severity === 'success' ? { 'aria-live': 'polite' as const } : {})}
      className={cn('rounded-md px-2.5 py-1.5 text-xs', SEVERITY_CLASS[severity])}
    >
      {result.msg}
    </div>
  );
}
